/**
 * Axios Cache Interceptor
 * Lightweight caching for GET requests without external dependencies
 * 
 * Features:
 * - Caches GET responses for 5 minutes by default
 * - Configurable cache time per endpoint
 * - Manual cache clearing
 * - Automatic cache invalidation on mutations (POST/PUT/DELETE)
 * 
 * Usage:
 *   import { setupApiCache } from '@/utils/api-cache'
 *   const axios = setupApiCache(axiosInstance)
 */

// Cache storage
const cache = new Map()
const cacheTimers = new Map()

// Default cache configuration
const DEFAULT_CACHE_CONFIG = {
  maxAge: 5 * 60 * 1000, // 5 minutes
  // Endpoints that should never be cached
  excludePaths: [
    '/api/auth/login',
    '/api/auth/logout',
    '/api/auth/forgot-password',
    '/api/auth/reset-password',
    '/api/auth/load', // Excluded to ensure fresh auth on page reload
    '/api/order',
    '/api/payment',
    '/api/subscription/create',
    '/api/subscription/update',
  ],
  // Endpoints with custom cache times
  customTTL: {
    '/api/category': 60 * 60 * 1000, // 1 hour
    '/api/course': 30 * 60 * 1000, // 30 minutes
  }
}

/**
 * Generate cache key from request
 */
function getCacheKey(config) {
  const { method, url, params } = config
  const queryStr = params ? JSON.stringify(params) : ''
  return `${method}:${url}:${queryStr}`
}

/**
 * Check if endpoint should be cached
 */
function shouldCache(config, cacheConfig = DEFAULT_CACHE_CONFIG) {
  const { method, url } = config
  
  // Only cache GET requests
  if (method !== 'get' && method !== 'GET') {
    return false
  }
  
  // Check if URL is in exclude list
  const isExcluded = cacheConfig.excludePaths.some(path => url.includes(path))
  if (isExcluded) {
    return false
  }
  
  return true
}

/**
 * Get cache duration for URL
 */
function getCacheDuration(url, cacheConfig = DEFAULT_CACHE_CONFIG) {
  // Check custom TTL
  for (const [path, ttl] of Object.entries(cacheConfig.customTTL)) {
    if (url.includes(path)) {
      return ttl
    }
  }
  
  return cacheConfig.maxAge
}

/**
 * Setup cache interceptors on axios instance
 */
export function setupApiCache(axiosInstance, cacheConfig = DEFAULT_CACHE_CONFIG) {
  
  // Response interceptor - cache successful GET responses
  axiosInstance.interceptors.response.use(
    response => {
      const cacheKey = getCacheKey(response.config)
      
      if (shouldCache(response.config, cacheConfig)) {
        const cacheDuration = getCacheDuration(response.config.url, cacheConfig)
        
        // Store in cache
        cache.set(cacheKey, response.data)
        
        // Clear old timer if exists
        if (cacheTimers.has(cacheKey)) {
          clearTimeout(cacheTimers.get(cacheKey))
        }
        
        // Set new timer to clear cache after duration
        const timer = setTimeout(() => {
          cache.delete(cacheKey)
          cacheTimers.delete(cacheKey)
        }, cacheDuration)
        
        cacheTimers.set(cacheKey, timer)
        
        if (process.env.NODE_ENV === 'development') {
          console.log(`[CACHE] Cached ${response.config.url} for ${cacheDuration / 60000} min`)
        }
      }
      
      return response
    },
    error => Promise.reject(error)
  )
  
  // Request interceptor - return cached response if available
  axiosInstance.interceptors.request.use(
    config => {
      if (shouldCache(config, cacheConfig)) {
        const cacheKey = getCacheKey(config)
        
        if (cache.has(cacheKey)) {
          if (process.env.NODE_ENV === 'development') {
            console.log(`[CACHE HIT] ${config.url}`)
          }
          
          // Return cached response
          return Promise.resolve({
            data: cache.get(cacheKey),
            status: 200,
            statusText: 'OK (from cache)',
            headers: {},
            config: config,
            request: null,
          })
        }
      }
      
      return config
    },
    error => Promise.reject(error)
  )
  
  // Add cache control methods
  axiosInstance.cache = {
    clear() {
      // Clear all cached data
      for (const timer of cacheTimers.values()) {
        clearTimeout(timer)
      }
      cache.clear()
      cacheTimers.clear()
      console.log('[CACHE] All cache cleared')
    },
    
    clearByPattern(pattern) {
      // Clear cache entries matching pattern
      for (const [key] of cache) {
        if (key.includes(pattern)) {
          cache.delete(key)
          if (cacheTimers.has(key)) {
            clearTimeout(cacheTimers.get(key))
            cacheTimers.delete(key)
          }
        }
      }
      console.log(`[CACHE] Cleared entries matching: ${pattern}`)
    },
    
    getStats() {
      return {
        totalCached: cache.size,
        entries: Array.from(cache.keys())
      }
    }
  }
  
  // Invalidate cache on mutations
  axiosInstance.interceptors.response.use(
    response => {
      const { method, url } = response.config
      
      if (['post', 'put', 'delete', 'patch'].includes(method.toLowerCase())) {
        // Clear related caches
        if (process.env.NODE_ENV === 'development') {
          console.log(`[CACHE] Invalidating cache after ${method.toUpperCase()} ${url}`)
        }
        
        // Extract resource type from URL and clear related caches
        const resourceMatch = url.match(/\/api\/(\w+)/)
        if (resourceMatch) {
          axiosInstance.cache.clearByPattern(`/api/${resourceMatch[1]}`)
        }
      }
      
      return response
    },
    error => Promise.reject(error)
  )
  
  return axiosInstance
}

export default {
  setupApiCache
}
